import Heading from 'components/Heading';
import RecordRow from 'components/RecordRow';
import Spacing from 'components/Spacing';
import { CalendarDay, numberToCurrencyString } from 'further-ui/utils';
import { CommitmentType, CurrencyCode } from 'further-types/lp/vehicle';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  styled,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PreviewTable from 'components/PreviewTable';
import React from 'react';
import { useLayoutContext } from 'contexts/LayoutContext';

const Container = styled(Spacing)(({ theme }) => ({
  borderTop: `1px solid ${theme.palette.borderColor?.main}`,
  paddingTop: theme.spacing(6),
}));

const StyledAccordion = styled(Accordion)({
  width: '100%',
  borderColor: '#D8D8D8',
});

const AccordionHeader = styled(AccordionSummary)(({ theme }) => ({
  padding: theme.spacing(4),
  color: theme.palette.text.darkTitle,
  fontSize: theme.fontSizes?.regular,
  fontWeight: 'bold',

  '& .MuiAccordionSummary-content': {
    margin: '0 !important',
  },
}));

const AccordionBody = styled(AccordionDetails)(({ theme }) => ({
  backgroundColor: theme.palette.background.header,
  padding: 0,
  border: 'none',
}));

type Props = {
  baseCurrency: CurrencyCode;
  equalisations: Array<{
    id: string;
    counterpartyLabel: string;
    closeDate: CalendarDay;
    numberOfNewCommitments: number;
    allocations: Array<{
      investorLabel: string;
      unpaidAllocationAmountLcy: number;
      totalFeesAmountLcy?: number;
      totalEqualisationInterestAmountLcy?: number;
      netEqualisationPayableReceivable?: number;
    }>;
    capitalContributionChangeLcy: number;
    selectedCommitments: Array<{
      commitmentId: string;
      investorName: string;
      isCarryPartnerCommitment: boolean;
      totalCommitmentLcy: number;
      loanCommitmentLcy: number;
      capitalCommitmentLcy: number;
      capitalContributionChange: number;
      existingCapitalCommitmentLcy?: number | undefined;
      equalisationDate: CalendarDay | null;
      isCapitalContributionRuleOverride?: boolean | undefined;
    }>;
    uncalledAmountLcy: number;
  }>;
};

const EqualisationsDetails: React.FC<Props> = ({
  equalisations,
  baseCurrency,
}) => {
  const { vehicle } = useLayoutContext();
  const equalisationAllocationColumns = [
    {
      label: 'Investor',
      key: 'investorLabel',
    },
    {
      label: 'Net equalisation payable / (receivable)',
      key: 'netEqualisationPayableReceivable',
    },
    {
      label: 'Equalisation interest payable / (receivable)',
      key: 'totalEqualisationInterestAmountLcy',
    },
    {
      label: 'Management fees',
      key: 'totalFeesAmountLcy',
    },
    {
      label: 'Total payable / (receivable)',
      key: 'unpaidAllocationAmountLcy',
    },
  ];

  const capitalContributionChangeColumns = [
    {
      key: 'investorName',
      label: 'Investor name',
    },
    {
      key: 'originalCapital',
      label: 'Original capital contribution',
    },
    {
      key: 'newCapital',
      label: 'New capital contribution',
    },
    {
      key: 'change',
      label: 'Change',
    },
  ];

  const isCapitalAndLoan =
    vehicle?.commitmentType === CommitmentType.CapitalAndLoan;

  return (
    <Spacing>
      {equalisations.map((equalisation, index) => {
        const equalisationAllocationRows = equalisation.allocations.map(
          (allocation) => ({
            investorLabel: allocation.investorLabel,
            netEqualisationPayableReceivable: numberToCurrencyString(
              allocation.netEqualisationPayableReceivable,
              {
                currency: baseCurrency,
                negativeInBrackets: true,
              },
            ),
            totalEqualisationInterestAmountLcy: numberToCurrencyString(
              allocation.totalEqualisationInterestAmountLcy,
              {
                currency: baseCurrency,
                negativeInBrackets: true,
              },
            ),
            totalFeesAmountLcy: numberToCurrencyString(
              allocation.totalFeesAmountLcy,
              {
                currency: baseCurrency,
              },
            ),
            unpaidAllocationAmountLcy: numberToCurrencyString(
              allocation.unpaidAllocationAmountLcy,
              {
                currency: baseCurrency,
                negativeInBrackets: true,
              },
            ),
          }),
        );

        const capitalContributionChangeRows =
          equalisation.selectedCommitments.map((commitment) => {
            const originalCapital = commitment.existingCapitalCommitmentLcy;
            const newCapital = commitment.capitalCommitmentLcy;

            return {
              investorName: commitment.investorName,
              originalCapital: numberToCurrencyString(originalCapital, {
                fallback: '-',
                currency: baseCurrency,
              }),
              newCapital: numberToCurrencyString(newCapital, {
                fallback: '-',
                currency: baseCurrency,
              }),
              change: numberToCurrencyString(
                commitment.capitalContributionChange,
                {
                  fallback: '-',
                  currency: baseCurrency,
                  negativeInBrackets: true,
                },
              ),
            };
          });

        return (
          <Spacing key={equalisation.id}>
            <Heading variant="h4" noMargin>
              Equalisation {index + 1}: {equalisation.counterpartyLabel} (
              {equalisation.closeDate.toLabel()})
            </Heading>
            <Spacing size="sm">
              <RecordRow label="Counterparty">
                {equalisation.counterpartyLabel}
              </RecordRow>
              <RecordRow label="Close date">
                {equalisation.closeDate.toLabel()}
              </RecordRow>
              <RecordRow label="Number of new commitments">
                {equalisation.numberOfNewCommitments}
              </RecordRow>
              {isCapitalAndLoan && (
                <RecordRow label="Capital contribution change">
                  {numberToCurrencyString(
                    equalisation.capitalContributionChangeLcy,
                    {
                      currency: baseCurrency,
                    },
                  )}
                </RecordRow>
              )}
              <RecordRow label="Uncalled amount">
                {numberToCurrencyString(equalisation.uncalledAmountLcy, {
                  currency: baseCurrency,
                })}
              </RecordRow>
            </Spacing>
            {isCapitalAndLoan && (
              <Container>
                <StyledAccordion defaultExpanded>
                  <AccordionHeader expandIcon={<ExpandMoreIcon />}>
                    Capital contribution changes
                  </AccordionHeader>
                  <AccordionBody>
                    <PreviewTable
                      rows={capitalContributionChangeRows}
                      columns={capitalContributionChangeColumns}
                    />
                  </AccordionBody>
                </StyledAccordion>
              </Container>
            )}
            <Container>
              <StyledAccordion defaultExpanded>
                <AccordionHeader expandIcon={<ExpandMoreIcon />}>
                  Equalisation allocation
                </AccordionHeader>
                <AccordionBody>
                  <PreviewTable
                    rows={equalisationAllocationRows}
                    columns={equalisationAllocationColumns}
                  />
                </AccordionBody>
              </StyledAccordion>
            </Container>
          </Spacing>
        );
      })}
    </Spacing>
  );
};

export default EqualisationsDetails;
